import { QueryDocumentSnapshot } from 'firebase/firestore';
import { Employee } from '@/types/resources/employee';
import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  limit, 
  startAfter,
  setDoc,
  arrayUnion,
  writeBatch,
  getCountFromServer
} from 'firebase/firestore';
import { addWorkspaceAndActivity, createActivityRecord, createDetailedActivityRecord, isDemoWorkspace } from '@/utils/apiHelpers';
import { Language, Currency } from '@/types/users';
import { User } from '../types/users';
import type { ActivityType } from '@/types/common';
import { deleteUser } from './users';
import { formatFirestoreTimestamp } from '@/utils/date';
import { removeUndefined } from '@/utils/removeUndefined';
import { loadMockData } from '@/mock';


// Helper function to filter mock data
function filterMockData<T extends { user: { displayName: string }, employeeId: string, department: string, role: string, status: string }>(
  data: T[],
  searchTerm?: string,
  status?: string,
  department?: string,
  role?: string,
): T[] {
  let filteredData = data;
  if (searchTerm) {
    const lowercasedSearch = searchTerm.toLowerCase();
    filteredData = filteredData.filter(emp => 
      emp.user.displayName.toLowerCase().includes(lowercasedSearch) ||
      emp.employeeId.toLowerCase().includes(lowercasedSearch) ||
      emp.department.toLowerCase().includes(lowercasedSearch) ||
      emp.role.toLowerCase().includes(lowercasedSearch)
    );
  }
  if (status) {
    filteredData = filteredData.filter(emp => emp.status === status);
  }
  if (department) {
    filteredData = filteredData.filter(emp => emp.department === department);
  }
  if (role) {
    filteredData = filteredData.filter(emp => emp.role === role);
  }
  return filteredData;
}

// Helper function to simulate pagination for mock data
function paginateMockData<T>(
  data: T[],
  pageSize?: number,
  currentPage: number = 1
): { data: T[]; total: number } {
  if (!pageSize) {
    return { data, total: data.length };
  }
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = data.slice(startIndex, endIndex);
  return {
    data: paginatedData,
    total: data.length
  };
}


// Constants
const EMPLOYEES_COLLECTION = 'employees';

// Types
export interface EmployeesQueryParams {
  searchTerm?: string;
  status?: string;
  department?: string;
  role?: string;
  pageSize?: number;
  page?: number; // Add page parameter
  lastDoc?: QueryDocumentSnapshot;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  firestoreUser: User;
}

export interface CreateEmployeeInput extends Omit<Employee, 'id' | 'user'> {
  user: {
    email: string;
    displayName: string;
    username: string;
    language: Language;
    currency: Currency;
    phoneNumber: string;
  };
  existingUser?: User; // Complete user object if found during email lookup
}

// Employee API
export async function getEmployees({
  searchTerm,
  status,
  department,
  role,
  pageSize,
  page = 1,
  lastDoc, // <-- Added back
  firestoreUser
}: EmployeesQueryParams): Promise<{ employees: Employee[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Use imported mock data
    const mockEmployees = await loadMockData<Employee[]>('employees', firestoreUser.language);
    const filteredData = filterMockData(mockEmployees, searchTerm, status, department, role);
    const paginatedData = paginateMockData(filteredData, pageSize, page);
    
    return {
      employees: paginatedData.data,
      lastDoc: undefined, // Mock data doesn't have Firestore documents
      total: paginatedData.total
    };
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const employeesRef = collection(db, EMPLOYEES_COLLECTION);
    const filterConstraints = [where('workspace.id', '==', firestoreUser.workspace.id)];

    if (searchTerm) {
      filterConstraints.push(
        where('name', '>=', searchTerm),
        where('name', '<=', searchTerm + '\uf8ff')
      );
    }

    if (status) {
      filterConstraints.push(where('status', '==', status));
    }

    if (department) {
      filterConstraints.push(where('department', '==', department));
    }

    if (role) {
      filterConstraints.push(where('role', '==', role));
    }

    let q = query(employeesRef, ...filterConstraints);

    if (pageSize) {
      q = query(q, limit(pageSize));
    }

    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const snapshot = await getDocs(q);
    const employees = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        joiningDate: formatFirestoreTimestamp(data.joiningDate),
        exitDate: formatFirestoreTimestamp(data.exitDate)
      } as Employee;
    });

    // Get total count using aggregation query
    const totalQuery = query(employeesRef, ...filterConstraints);
    const totalSnapshot = await getCountFromServer(totalQuery);
    const total = totalSnapshot.data().count;

    return {
      employees,
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
      total: total
    };
  } catch (error) {
    console.error('Error fetching employees:', error);
    throw new Error('Failed to fetch employees');
  }
}

export async function getEmployeeById(id: string, firestoreUser: User): Promise<Employee | null> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Use imported mock data
    const mockEmployees = await loadMockData<Employee[]>('employees', firestoreUser.language);
    const employee = mockEmployees.find(emp => emp.id === id) || null;
    return employee;
  }

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get the employee document directly by ID
    const employeeRef = doc(db, EMPLOYEES_COLLECTION, id);
    const employeeDoc = await getDoc(employeeRef);

    if (!employeeDoc.exists()) {
      return null;
    }

    const employeeData = employeeDoc.data();
    
    // Check if the employee belongs to the current workspace
    if (employeeData.workspace.id !== firestoreUser.workspace.id) {
      return null;
    }

    return {
      ...employeeData,
      id: employeeDoc.id,
      joiningDate: formatFirestoreTimestamp(employeeData.joiningDate),
      exitDate: formatFirestoreTimestamp(employeeData.exitDate)
    } as Employee;
  } catch (error) {
    console.error('Error fetching employee:', error);
    throw new Error('Failed to fetch employee');
  }
}

export async function addEmployee(
  employee: CreateEmployeeInput,
  firestoreUser: User
): Promise<string> {
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    let userToUse: User;

    if (employee.existingUser) {
      userToUse = employee.existingUser;
    } else {
      // Create a new user document
      const userRef = doc(collection(db, 'users'));
      const newUser: User = {
        id: userRef.id,
        email: employee.user.email,
        displayName: employee.user.displayName,
        username: employee.user.username,
        language: employee.user.language,
        currency: employee.user.currency,
        phoneNumber: employee.user.phoneNumber,
        workspace: firestoreUser.workspace,
        roles: ['employee'],
        status: 'active',
        contacts: [],
        createdAt: new Date(),
      };

      await setDoc(userRef, newUser);
      userToUse = newUser;
    }

    const employeeData = addWorkspaceAndActivity(
      {
        ...employee,
        user: {
          id: userToUse.id,
          email: userToUse.email,
          displayName: userToUse.displayName,
          username: userToUse.username,
          language: userToUse.language,
          currency: userToUse.currency,
          phoneNumber: userToUse.phoneNumber
        },
        createdAt: new Date(),
        updatedAt: new Date()
      },
      firestoreUser,
      'created'
    );

    const employeeRef = doc(collection(db, EMPLOYEES_COLLECTION));
    await setDoc(employeeRef, employeeData);
    return employeeRef.id;
  } catch (error) {
    console.error('Error adding employee:', error);
    throw new Error('Failed to add employee');
  }
}

export async function updateEmployee(
  id: string,
  data: Partial<Employee>,
  firestoreUser: User
): Promise<void> {
  try {
    const docRef = doc(db, EMPLOYEES_COLLECTION, id);
    const employeeDoc = await getDoc(docRef);

    if (!employeeDoc.exists()) {
      throw new Error('Employee not found');
    }

    const existingData = employeeDoc.data() as Employee;
    let activityType: ActivityType = 'updated';
    if (data.invited === true) {
      activityType = 'invited';
    }
    const activityRecord = createDetailedActivityRecord(activityType, existingData, data, firestoreUser);

    // Remove activityRecords from data to avoid conflicts
    const { activityRecords, ...updateData } = data;
    
    // Prepare the update data without activityRecords
    const cleanData = removeUndefined({
      ...updateData,
      joiningDate: formatFirestoreTimestamp(data.joiningDate),
      exitDate: formatFirestoreTimestamp(data.exitDate)
    });
    
    // Add activity record separately
    const updatePayload = {
      ...cleanData,
      activityRecords: arrayUnion(activityRecord)
    };

    await updateDoc(docRef, updatePayload);
  } catch (error) {
    console.error('Error updating employee:', error);
    throw new Error('Failed to update employee');
  }
}

export async function deleteEmployee(
  id: string,
  firestoreUser: User
): Promise<void> {
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get the employee document directly
    const employeeRef = doc(db, EMPLOYEES_COLLECTION, id);
    const employeeDoc = await getDoc(employeeRef);

    if (!employeeDoc.exists()) {
      throw new Error('Employee not found');
    }

    const employeeData = employeeDoc.data();
    if (employeeData.workspace.id !== firestoreUser.workspace.id) {
      throw new Error('Employee not found in current workspace');
    }

    // Check for dependencies before deletion
    const dependencies: { type: string; items: any[] }[] = [];

    // Check if employee has a user account
    if (employeeData.user && employeeData.user.id) {
      dependencies.push({
        type: 'userAccount',
        items: [{
          id: employeeData.user.id,
          name: employeeData.user.displayName || 'User Account',
          email: employeeData.user.email || 'No email'
        }]
      });
    }

    // Check if employee has active attendance records
    const attendanceRef = collection(db, ATTENDANCE_COLLECTION);
    const attendanceQuery = query(
      attendanceRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('employee.id', '==', id)
    );
    const attendanceSnapshot = await getDocs(attendanceQuery);
    
    if (!attendanceSnapshot.empty) {
      const attendanceRecords = attendanceSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      dependencies.push({
        type: 'attendance',
        items: attendanceRecords
      });
    }

    // Check if employee has active leave requests
    const leaveRequestsRef = collection(db, LEAVE_REQUESTS_COLLECTION);
    const leaveRequestsQuery = query(
      leaveRequestsRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('employee.id', '==', id),
      where('status', 'in', ['pending', 'approved'])
    );
    const leaveRequestsSnapshot = await getDocs(leaveRequestsQuery);
    
    if (!leaveRequestsSnapshot.empty) {
      const leaveRequests = leaveRequestsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      dependencies.push({
        type: 'leaveRequests',
        items: leaveRequests
      });
    }

    // If there are dependencies, throw detailed error
    if (dependencies.length > 0) {
      const error = new Error('Cannot delete employee with dependencies');
      (error as any).dependencies = dependencies;
      (error as any).employeeName = employeeData.user?.displayName || employeeData.id;
      throw error;
    }

    const activityRecord = createActivityRecord('deleted', employeeData, firestoreUser);

    // Add the activity record before deletion
    await updateDoc(employeeRef, {
      activityRecords: arrayUnion(activityRecord)
    });

    // Delete the employee document
    await deleteDoc(employeeRef);

    // Delete the associated user document
    if (employeeData.user && employeeData.user.id) {
      await deleteUser(employeeData.user.id, firestoreUser);
    }
  } catch (error) {
    console.error('Error deleting employee:', error);
    throw error;
  }
}

export async function forceDeleteEmployee(
  id: string,
  firestoreUser: User
): Promise<void> {
  console.log('API Call: forceDeleteEmployee', { id, firestoreUser });
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get the employee document directly
    const employeeRef = doc(db, EMPLOYEES_COLLECTION, id);
    const employeeDoc = await getDoc(employeeRef);

    if (!employeeDoc.exists()) {
      throw new Error('Employee not found');
    }

    const employeeData = employeeDoc.data();
    if (employeeData.workspace.id !== firestoreUser.workspace.id) {
      throw new Error('Employee not found in current workspace');
    }

    // Force delete - clean up all dependencies first
    const batch = writeBatch(db);

    // Delete all attendance records for this employee
    const attendanceRef = collection(db, ATTENDANCE_COLLECTION);
    const attendanceQuery = query(
      attendanceRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('employee.id', '==', id)
    );
    const attendanceSnapshot = await getDocs(attendanceQuery);
    
    attendanceSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // Delete all leave requests for this employee
    const leaveRequestsRef = collection(db, LEAVE_REQUESTS_COLLECTION);
    const leaveRequestsQuery = query(
      leaveRequestsRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('employee.id', '==', id)
    );
    const leaveRequestsSnapshot = await getDocs(leaveRequestsQuery);
    
    leaveRequestsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // Delete the associated user document
    if (employeeData.user && employeeData.user.id) {
      await deleteUser(employeeData.user.id, firestoreUser);
    }

    const activityRecord = createActivityRecord('deleted', employeeData, firestoreUser);

    // Add the activity record before deletion
    batch.update(employeeRef, {
      activityRecords: arrayUnion(activityRecord)
    });

    // Delete the employee document
    batch.delete(employeeRef);

    // Commit all changes
    await batch.commit();
    
    console.log('Force deleted employee and all dependencies');
  } catch (error) {
    console.error('Error force deleting employee:', error);
    throw error;
  }
}

// Additional helper functions
export async function getEmployeesByDepartment(department: string, firestoreUser: User): Promise<Employee[]> {
  if (isDemoWorkspace(firestoreUser)) {
    // Use imported mock data
    const mockEmployees = await loadMockData<Employee[]>('employees', firestoreUser.language);
    return mockEmployees.filter(emp => emp.department === department);
  }
  const { employees } = await getEmployees({ department, firestoreUser });
  return employees;
}

export async function getEmployeesByRole(role: string, firestoreUser: User): Promise<Employee[]> {
  if (isDemoWorkspace(firestoreUser)) {
    // Use imported mock data
    const mockEmployees = await loadMockData<Employee[]>('employees', firestoreUser.language);
    return mockEmployees.filter(emp => emp.role === role);
  }
  const { employees } = await getEmployees({ role, firestoreUser });
  return employees;
}

export async function getEmployeesByStatus(status: string, firestoreUser: User): Promise<Employee[]> {
  if (isDemoWorkspace(firestoreUser)) {
    // Use imported mock data
    const mockEmployees = await loadMockData<Employee[]>('employees', firestoreUser.language);
    return mockEmployees.filter(emp => emp.status === status);
  }
  const { employees } = await getEmployees({ status, firestoreUser });
  return employees;
}

// Attendance APIs
const ATTENDANCE_COLLECTION = 'attendance';
const LEAVE_REQUESTS_COLLECTION = 'leave_requests';

export interface AttendanceQueryParams {
  date?: string; // YYYY-MM-DD format
  pageSize?: number;
  lastDoc?: QueryDocumentSnapshot;
  firestoreUser: User;
}

export interface CreateAttendanceInput {
  employee: {
    id: string;
    name: string;
    checkInTime?: Date;
    checkOutTime?: Date;
    status: 'Present' | 'Absent' | 'Late' | 'On Leave' | 'Half Day';
  }[];
  day: number; // 1-31
  month: number; // 1-12
  year: number; // e.g., 2025
  notes?: string;
}

export interface UpdateAttendanceInput extends Partial<CreateAttendanceInput> {
  id: string;
}

export async function getAttendanceRecord(
  day: number,
  month: number,
  year: number,
  firestoreUser: User
): Promise<any | null> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Import mock data dynamically to avoid circular dependencies
    const attendances = await loadMockData<any[]>('attendances', firestoreUser.language);
    
    const record = attendances.find(att => 
      att.day === day && att.month === month && att.year === year
    );
    
    return record || null;
  }

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const attendanceRef = collection(db, ATTENDANCE_COLLECTION);
    const q = query(
      attendanceRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('day', '==', day),
      where('month', '==', month),
      where('year', '==', year)
    );

    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    const result = {
      id: doc.id,
      ...doc.data()
    };
    
    return result;
  } catch (error) {
    console.error('Error fetching attendance record:', error);
    throw new Error('Failed to fetch attendance record');
  }
}

export async function createAttendanceRecord(
  attendance: CreateAttendanceInput,
  firestoreUser: User
): Promise<string> {
  try {
    const attendanceData = addWorkspaceAndActivity(
      {
        ...attendance,
        createdAt: new Date(),
        updatedAt: new Date()
      },
      firestoreUser,
      'created'
    );

    const attendanceRef = doc(collection(db, ATTENDANCE_COLLECTION));
    await setDoc(attendanceRef, attendanceData);
    return attendanceRef.id;
  } catch (error) {
    console.error('Error creating attendance record:', error);
    throw new Error('Failed to create attendance record');
  }
}

export async function updateAttendanceRecord(
  id: string,
  data: Partial<CreateAttendanceInput> & { activityRecords?: any[] },
  firestoreUser: User
): Promise<void> {
  try {
    const docRef = doc(db, ATTENDANCE_COLLECTION, id);
    const attendanceDoc = await getDoc(docRef);

    if (!attendanceDoc.exists()) {
      throw new Error('Attendance record not found');
    }

    const existingData = attendanceDoc.data();
    const activityRecord = createDetailedActivityRecord('updated', existingData, data, firestoreUser);

    const { activityRecords, ...updateData } = data;
    
    const cleanData = removeUndefined({
      ...updateData,
      updatedAt: new Date()
    });
    
    const updatePayload = {
      ...cleanData,
      activityRecords: arrayUnion(activityRecord)
    };

    await updateDoc(docRef, updatePayload);
  } catch (error) {
    console.error('Error updating attendance record:', error);
    throw new Error('Failed to update attendance record');
  }
}

export async function deleteAttendanceRecord(
  id: string,
  firestoreUser: User
): Promise<void> {
  try {
    const docRef = doc(db, ATTENDANCE_COLLECTION, id);
    const attendanceDoc = await getDoc(docRef);

    if (!attendanceDoc.exists()) {
      throw new Error('Attendance record not found');
    }

    const attendanceData = attendanceDoc.data();
    const activityRecord = createActivityRecord('deleted', attendanceData, firestoreUser);

    await updateDoc(docRef, {
      activityRecords: arrayUnion(activityRecord)
    });

    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting attendance record:', error);
    throw new Error('Failed to delete attendance record');
  }
}

// Leave Request APIs
export interface LeaveRequestQueryParams {
  employeeId?: string;
  status?: string;
  pageSize?: number;
  page?: number;
  lastDoc?: QueryDocumentSnapshot;
  firestoreUser: User;
  searchTerm?: string;
}

export interface CreateLeaveRequestInput {
  employee: {
    id: string;
    name: string;
  };
  startDate: Date;
  endDate: Date;
  type: 'Annual' | 'Sick' | 'Unpaid' | 'Other';
  reason: string;
}

export interface UpdateLeaveRequestInput extends Partial<CreateLeaveRequestInput> {
  id: string;
  status?: 'Pending' | 'Approved' | 'Rejected';
  approvedBy?: {
    id: string;
    name: string;
  };
}

export async function getLeaveRequests({
  employeeId,
  status,
  pageSize,
  page = 1,
  lastDoc,
  firestoreUser,
  searchTerm
}: LeaveRequestQueryParams): Promise<{ requests: any[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
      // Demo mode check
    if (isDemoWorkspace(firestoreUser)) {
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Import mock data dynamically to avoid circular dependencies
      const leaveRequests = await loadMockData<any[]>('leaveRequests', firestoreUser.language);
      
      let filteredData = leaveRequests;
      
      if (employeeId) {
        filteredData = filteredData.filter(req => req.employee.id === employeeId);
      }
      
      if (status) {
        filteredData = filteredData.filter(req => req.status === status);
      }
      
      if (searchTerm) {
        filteredData = filteredData.filter(req => 
          req.employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req.reason.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      const paginatedData = paginateMockData(filteredData, pageSize, page);
      
      return {
        requests: paginatedData.data,
        lastDoc: undefined,
        total: paginatedData.total
      };
    }

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const leaveRequestsRef = collection(db, LEAVE_REQUESTS_COLLECTION);
    const filterConstraints = [where('workspace.id', '==', firestoreUser.workspace.id)];

    if (employeeId) {
      filterConstraints.push(where('employee.id', '==', employeeId));
    }

    if (status) {
      filterConstraints.push(where('status', '==', status));
    }

    let q = query(leaveRequestsRef, ...filterConstraints);

    if (pageSize) {
      q = query(q, limit(pageSize));
    }

    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const snapshot = await getDocs(q);
    const requests = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return {
      requests,
      lastDoc: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : undefined,
      total: requests.length
    };
  } catch (error) {
    console.error('Error fetching leave requests:', error);
    throw new Error('Failed to fetch leave requests');
  }
}

export async function createLeaveRequest(
  leaveRequest: CreateLeaveRequestInput,
  firestoreUser: User
): Promise<string> {
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const leaveRequestData = addWorkspaceAndActivity(
      {
        ...leaveRequest,
        status: 'Pending',
        createdAt: new Date(),
        updatedAt: new Date()
      },
      firestoreUser,
      'created'
    );

    const leaveRequestRef = doc(collection(db, LEAVE_REQUESTS_COLLECTION));
    await setDoc(leaveRequestRef, leaveRequestData);
    return leaveRequestRef.id;
  } catch (error) {
    console.error('Error creating leave request:', error);
    throw new Error('Failed to create leave request');
  }
}

export async function updateLeaveRequest(
  id: string,
  data: Partial<UpdateLeaveRequestInput> & { activityRecords?: any[] },
  firestoreUser: User
): Promise<void> {
  try {
    const docRef = doc(db, LEAVE_REQUESTS_COLLECTION, id);
    const leaveRequestDoc = await getDoc(docRef);

    if (!leaveRequestDoc.exists()) {
      throw new Error('Leave request not found');
    }

    const existingData = leaveRequestDoc.data();
    const activityRecord = createDetailedActivityRecord('updated', existingData, data, firestoreUser);

    const { activityRecords, ...updateData } = data;
    
    const cleanData = removeUndefined({
      ...updateData,
      updatedAt: new Date()
    });
    
    const updatePayload = {
      ...cleanData,
      activityRecords: arrayUnion(activityRecord)
    };

    await updateDoc(docRef, updatePayload);
  } catch (error) {
    console.error('Error updating leave request:', error);
    throw new Error('Failed to update leave request');
  }
}

export async function deleteLeaveRequest(
  id: string,
  firestoreUser: User
): Promise<void> {
  try {
    const docRef = doc(db, LEAVE_REQUESTS_COLLECTION, id);
    const leaveRequestDoc = await getDoc(docRef);

    if (!leaveRequestDoc.exists()) {
      throw new Error('Leave request not found');
    }

    const leaveRequestData = leaveRequestDoc.data();
    const activityRecord = createActivityRecord('deleted', leaveRequestData, firestoreUser);

    await updateDoc(docRef, {
      activityRecords: arrayUnion(activityRecord)
    });

    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting leave request:', error);
    throw new Error('Failed to delete leave request');
  }
}

// Utility functions for attendance queries
export async function getAttendanceByMonth(
  month: number,
  year: number,
  firestoreUser: User
): Promise<any[]> {
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const attendanceRef = collection(db, ATTENDANCE_COLLECTION);
    const q = query(
      attendanceRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('month', '==', month),
      where('year', '==', year)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching attendance by month:', error);
    throw new Error('Failed to fetch attendance by month');
  }
}

export async function getAttendanceByYear(
  year: number,
  firestoreUser: User
): Promise<any[]> {
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const attendanceRef = collection(db, ATTENDANCE_COLLECTION);
    const q = query(
      attendanceRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('year', '==', year)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching attendance by year:', error);
    throw new Error('Failed to fetch attendance by year');
  }
}

// Bulk upload employees using Firestore batch operations
export async function addEmployeesBulk(
  employees: Omit<Employee, 'id' | 'activityRecords' | 'workspace'>[], 
  firestoreUser: User,
  onProgress?: (uploaded: number, total: number) => void
): Promise<{ successCount: number; errorCount: number; errors: string[] }> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate progress for demo mode
    for (let i = 0; i < employees.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 100));
      onProgress?.(i + 1, employees.length);
    }
    return { successCount: employees.length, errorCount: 0, errors: [] };
  }
  
  try {
    const CHUNK_SIZE = 500; // Firestore batch limit
    const chunks = [];
    
    // Split records into chunks of 500
    for (let i = 0; i < employees.length; i += CHUNK_SIZE) {
      chunks.push(employees.slice(i, i + CHUNK_SIZE));
    }

    let totalUploaded = 0;
    const errors: string[] = [];
    
    // Process each chunk with progress updates
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex];
      const batch = writeBatch(db);
      
      chunk.forEach((employeeData) => {
        const employeeRef = doc(collection(db, EMPLOYEES_COLLECTION));
        const employeeWithMetadata = addWorkspaceAndActivity(employeeData, firestoreUser, 'created');
        const cleanedEmployee = removeUndefined(employeeWithMetadata);
        
        batch.set(employeeRef, cleanedEmployee);
      });

      try {
        await batch.commit();
        totalUploaded += chunk.length;
        onProgress?.(totalUploaded, employees.length);
      } catch (error) {
        console.error(`Error uploading chunk ${chunkIndex + 1}:`, error);
        errors.push(`Chunk ${chunkIndex + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    const successCount = totalUploaded;
    const errorCount = employees.length - totalUploaded;

    return { successCount, errorCount, errors };
  } catch (error) {
    console.error('Error in bulk upload:', error);
    throw new Error('Failed to upload employees in bulk');
  }
} 